import { useState, useCallback, useMemo } from "react";
import {
  Webhook,
  Plus,
  Trash2,
  List,
  Copy,
  Check,
  Send,
  Power,
  PowerOff,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import {
  useEnterpriseWebhooks,
  useCreateEnterpriseWebhook,
  useDeleteEnterpriseWebhook,
  useWebhookDeliveries,
} from "@/hooks/admin/useEnterprise";
import type {
  EnterpriseWebhook,
  WebhookDelivery,
  CreateWebhookInput,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AVAILABLE_EVENTS: readonly { readonly value: string; readonly label: string }[] = [
  { value: "order.created", label: "Commande creee" },
  { value: "order.updated", label: "Commande mise a jour" },
  { value: "payment.received", label: "Paiement recu" },
  { value: "delivery.started", label: "Livraison demarree" },
  { value: "delivery.completed", label: "Livraison terminee" },
  { value: "product.updated", label: "Produit mis a jour" },
  { value: "stock.low", label: "Stock bas" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function generateHmacSecret(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const length = 32;
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, (v) => chars[v % chars.length]).join("");
}

// ---------------------------------------------------------------------------
// Create Webhook Modal
// ---------------------------------------------------------------------------

interface CreateWebhookModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateWebhookInput) => Promise<void>;
}

function CreateWebhookModal({
  open,
  onClose,
  isSaving,
  onSave,
}: CreateWebhookModalProps) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<readonly string[]>([]);
  const [secret, setSecret] = useState(() => generateHmacSecret());
  const [secretCopied, setSecretCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setUrl("");
    setEvents([]);
    setSecret(generateHmacSecret());
    setSecretCopied(false);
    setError(null);
    onClose();
  }, [onClose]);

  const toggleEvent = useCallback((event: string) => {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  }, []);

  const handleCopySecret = useCallback(() => {
    navigator.clipboard.writeText(secret);
    setSecretCopied(true);
    toast.success("Secret HMAC copie");
    setTimeout(() => setSecretCopied(false), 2000);
  }, [secret]);

  const handleRegenerateSecret = useCallback(() => {
    setSecret(generateHmacSecret());
    setSecretCopied(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!url.trim()) {
      setError("L'URL est requise.");
      return;
    }
    if (!url.startsWith("https://")) {
      setError("L'URL doit commencer par https://");
      return;
    }
    if (events.length === 0) {
      setError("Selectionnez au moins un evenement.");
      return;
    }
    setError(null);
    try {
      await onSave({
        url: url.trim(),
        events,
        secret: secret.trim(),
      });
      toast.success("Webhook cree avec succes");
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(message);
      toast.error("Echec de la creation du webhook");
    }
  }, [url, events, secret, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Ajouter un webhook"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Creation..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* URL */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="https://example.com/webhook"
          />
          <p className="mt-1 text-xs text-stone-400">
            Seules les URL HTTPS sont acceptees.
          </p>
        </div>

        {/* Events */}
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Evenements <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {AVAILABLE_EVENTS.map((event) => (
              <label
                key={event.value}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors ${
                  events.includes(event.value)
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={events.includes(event.value)}
                  onChange={() => toggleEvent(event.value)}
                  className="rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <code className="text-xs">{event.value}</code>
                  <p className="text-xs text-stone-500">{event.label}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* HMAC Secret */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Secret HMAC
          </label>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-md border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs text-stone-700">
              {secret}
            </code>
            <button
              type="button"
              onClick={handleCopySecret}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-stone-200 bg-white text-stone-600 transition-colors hover:bg-stone-50"
              title="Copier"
            >
              {secretCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={handleRegenerateSecret}
              className="rounded-md border border-stone-200 bg-white px-2 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-50"
            >
              Regenerer
            </button>
          </div>
          <p className="mt-1 text-xs text-stone-400">
            Utilise pour valider les requetes entrantes via HMAC-SHA256. Genere automatiquement.
          </p>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Delivery History Modal
// ---------------------------------------------------------------------------

interface DeliveryHistoryModalProps {
  readonly webhookId: string | null;
  readonly webhookUrl: string | null;
  readonly onClose: () => void;
}

function DeliveryHistoryModal({ webhookId, webhookUrl, onClose }: DeliveryHistoryModalProps) {
  const { data, isLoading } = useWebhookDeliveries(webhookId ?? "");

  const deliveries = data?.deliveries ?? [];

  const deliveryColumns: readonly ColumnDef<WebhookDelivery>[] = [
    {
      id: "event",
      header: "Evenement",
      cell: (row) => (
        <code className="rounded bg-stone-100 px-1.5 py-0.5 text-xs text-stone-700">
          {row.event}
        </code>
      ),
    },
    {
      id: "status_code",
      header: "Code HTTP",
      cell: (row) => (
        <span
          className={`font-mono text-sm font-medium ${
            row.success ? "text-emerald-700" : "text-red-700"
          }`}
        >
          {row.status_code}
        </span>
      ),
    },
    {
      id: "success",
      header: "Resultat",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            row.success
              ? "border-emerald-200 bg-emerald-100 text-emerald-700"
              : "border-red-200 bg-red-100 text-red-700"
          }`}
        >
          {row.success ? "Succes" : "Echec"}
        </span>
      ),
    },
    {
      id: "response_time_ms",
      header: "Temps de reponse",
      cell: (row) => {
        const color =
          row.response_time_ms > 2000
            ? "text-red-600"
            : row.response_time_ms > 500
            ? "text-amber-600"
            : "text-stone-600";
        return <span className={`text-sm font-medium ${color}`}>{row.response_time_ms} ms</span>;
      },
    },
    {
      id: "triggered_at",
      header: "Declenche le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDateTime(row.triggered_at)}</span>
      ),
    },
  ];

  return (
    <AdminModal
      open={Boolean(webhookId)}
      onClose={onClose}
      title="Historique des livraisons"
      size="xl"
      footer={<Button variant="ghost" onClick={onClose}>Fermer</Button>}
    >
      <div className="space-y-3">
        {webhookUrl && (
          <p className="text-xs text-stone-500">
            Webhook : <code className="rounded bg-stone-100 px-1.5 py-0.5">{webhookUrl}</code>
          </p>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-200 border-t-amber-500" />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="flex flex-col items-center py-8">
            <List className="h-8 w-8 text-stone-300" />
            <p className="mt-2 text-sm text-stone-500">
              Aucune livraison enregistree.
            </p>
          </div>
        ) : (
          <AdminDataTable
            columns={deliveryColumns}
            data={deliveries}
            emptyMessage="Aucune livraison."
            isLoading={false}
          />
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Webhooks() {
  useAdminPageTitle("Webhooks");

  const [showCreate, setShowCreate] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<{
    readonly id: string;
    readonly url: string;
  } | null>(null);

  const { data, isLoading, isError } = useEnterpriseWebhooks();
  const createMutation = useCreateEnterpriseWebhook();
  const deleteMutation = useDeleteEnterpriseWebhook();

  const webhooks = data?.webhooks ?? [];

  const activeCount = useMemo(() => webhooks.filter((w) => w.is_active).length, [webhooks]);
  const failedCount = useMemo(
    () => webhooks.filter((w) => w.failure_count > 0).length,
    [webhooks],
  );
  const totalEvents = useMemo(
    () => webhooks.reduce((sum, w) => sum + w.events.length, 0),
    [webhooks],
  );

  const handleCreate = useCallback(
    async (input: CreateWebhookInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const handleDelete = useCallback(
    async (id: string, url: string) => {
      if (!confirm(`Supprimer le webhook "${url}" ? Cette action est irreversible.`)) return;
      try {
        await deleteMutation.mutateAsync(id);
        toast.success("Webhook supprime");
      } catch {
        toast.error("Echec de la suppression du webhook");
      }
    },
    [deleteMutation],
  );

  const handleTest = useCallback((url: string) => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: `Envoi du test vers ${url}...`,
        success: "Payload de test envoye avec succes",
        error: "Echec de l'envoi du test",
      },
    );
  }, []);

  const columns: readonly ColumnDef<EnterpriseWebhook>[] = [
    {
      id: "url",
      header: "URL",
      cell: (row) => (
        <div className="max-w-xs">
          <p className="truncate text-sm font-medium text-stone-900">{row.url}</p>
          <p className="mt-0.5 text-xs text-stone-400">
            {row.events.length} evenement{row.events.length > 1 ? "s" : ""}
          </p>
        </div>
      ),
    },
    {
      id: "events",
      header: "Evenements",
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.events.slice(0, 3).map((e) => (
            <code
              key={e}
              className="rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-xs text-stone-600"
            >
              {e}
            </code>
          ))}
          {row.events.length > 3 && (
            <span className="rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-xs text-stone-500">
              +{row.events.length - 3}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "is_active",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            row.is_active
              ? row.failure_count > 3
                ? "border-red-200 bg-red-100 text-red-700"
                : "border-emerald-200 bg-emerald-100 text-emerald-700"
              : "border-stone-200 bg-stone-100 text-stone-500"
          }`}
        >
          {row.is_active ? (
            row.failure_count > 3 ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                En echec
              </>
            ) : (
              <>
                <Power className="h-3 w-3" />
                Actif
              </>
            )
          ) : (
            <>
              <PowerOff className="h-3 w-3" />
              Inactif
            </>
          )}
        </span>
      ),
    },
    {
      id: "last_triggered_at",
      header: "Derniere livraison",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDateTime(row.last_triggered_at)}</span>
      ),
    },
    {
      id: "failure_count",
      header: "Echecs",
      cell: (row) => (
        <span
          className={`text-sm font-medium ${
            row.failure_count > 3
              ? "text-red-600"
              : row.failure_count > 0
              ? "text-amber-600"
              : "text-stone-400"
          }`}
        >
          {row.failure_count}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => handleTest(row.url)}
            className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
            title="Tester"
          >
            <Send className="h-3 w-3" />
            Tester
          </button>
          <button
            onClick={() => setSelectedWebhook({ id: row.id, url: row.url })}
            className="flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-50"
            title="Voir livraisons"
          >
            <List className="h-3 w-3" />
            Livraisons
          </button>
          <button
            onClick={() => handleDelete(row.id, row.url)}
            className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
            title="Supprimer"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ),
    },
  ];

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des webhooks
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Webhook className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">Webhooks</h1>
              <p className="text-sm text-stone-500">
                Notifications d'evenements en temps reel
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Ajouter un webhook
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4">
          <KPICard
            label="Webhooks actifs"
            value={activeCount}
            icon={<Power className="h-4 w-4" />}
          />
          <KPICard
            label="En echec"
            value={failedCount}
            icon={<ShieldAlert className="h-4 w-4" />}
          />
          <KPICard
            label="Evenements souscrits"
            value={totalEvents}
            icon={<Webhook className="h-4 w-4" />}
          />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={webhooks}
          isLoading={isLoading}
          emptyMessage="Aucun webhook configure."
        />
      </div>

      <CreateWebhookModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />

      <DeliveryHistoryModal
        webhookId={selectedWebhook?.id ?? null}
        webhookUrl={selectedWebhook?.url ?? null}
        onClose={() => setSelectedWebhook(null)}
      />
    </AdminLayout>
  );
}
